import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import IncidentResources from "../resources/IncidentResources";
import LocationResources from "../resources/LocationResources";
import { connect } from "react-redux";
import { logout } from "../redux/actions/LoggedInAction";
import Select from 'react-select';
import moment from "moment";
import {ACCESS_DENIED} from '../Messages';
import toast from 'react-hot-toast';

function ViewIncident({isLoggedIn, loggedInUser}){
    const navigate = useNavigate();

    const [incidents, setIncidents] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [showUnreviewedOnly, setShowUnreviewedOnly] = useState(true)
    const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'))
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'))
    const [selectedIncident, setSelectedIncident] = useState(null)
    const [showDetailsDialog, setShowDetailsDialog] = useState(false)
    const [editingIncident, setEditingIncident] = useState(null)
    const [editIssues, setEditIssues] = useState('')
    const [editComments, setEditComments] = useState('')
    const [newComment, setNewComment] = useState('')

    const incidentResources = new IncidentResources()
    const locationResources = new LocationResources()

    const loadLocations = () => {
        return locationResources.get()
    }

    const loadIncidents = () => {
        // Validate date range (max 12 months)
        const start = moment(startDate)
        const end = moment(endDate)
        const daysDiff = end.diff(start, 'days')

        if (daysDiff > 365) {
            toast.error("Date range cannot exceed 12 months (365 days)")
            return Promise.resolve({ error: "Date range too large" })
        }

        if (daysDiff < 0) {
            toast.error("End date must be after start date")
            return Promise.resolve({ error: "Invalid date range" })
        }

        const params = {
            start_date: moment(startDate).format('YYYY-MM-DD'),
            end_date: moment(endDate).format('YYYY-MM-DD')
        }

        if (selectedLocation) {
            params.location_id = selectedLocation.value
        }

        if (showUnreviewedOnly) {
            params.reviewed = 'false'
        }

        return incidentResources.get(null, params)
    }

    useEffect(() => {
        loadLocations().then(response => {
            if (response.error && response.error.length > 0) {
                toast.error("Failed to load locations: " + response.error)
            } else {
                const locationData = response['body'] || []
                const locationOptions = locationData.map(loc => ({
                    value: loc.id,
                    label: loc.name
                }))
                setLocations(locationOptions)
            }
        }).catch((err) => {
            toast.error("Network error loading locations: " + err)
        })

        // Load incidents on mount
        loadIncidents().then(response => {
            if (response.error && response.error.length > 0) {
                if (response.error !== "Date range too large" && response.error !== "Invalid date range") {
                    toast.error("Failed to load incidents: " + response.error)
                }
            } else {
                setIncidents(response['body'] || [])
            }
        }).catch((err) => {
            toast.error("Network error loading incidents: " + err)
        })
    }, [])

    const handleFilterChange = () => {
        loadIncidents().then(response => {
            if (response.error && response.error.length > 0) {
                if (response.error !== "Date range too large" && response.error !== "Invalid date range") {
                    toast.error("Failed to load incidents: " + response.error)
                }
            } else {
                setIncidents(response['body'] || [])
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    const handleLocationChange = (selectedOption) => {
        setSelectedLocation(selectedOption)
    }

    const handleViewDetails = (incident) => {
        setSelectedIncident(incident)
        setShowDetailsDialog(true)
        setEditingIncident(null)
        setNewComment('')
    }

    const handleCloseDetails = () => {
        setShowDetailsDialog(false)
        setSelectedIncident(null)
        setEditingIncident(null)
        setEditIssues('')
        setEditComments('')
        setNewComment('')
    }

    const handleStartEdit = () => {
        if (selectedIncident) {
            setEditingIncident(selectedIncident.id)
            setEditIssues(selectedIncident.issues || '')
            setEditComments((selectedIncident.comments && selectedIncident.comments !== 'None') ? selectedIncident.comments : '')
        }
    }

    const handleCancelEdit = () => {
        setEditingIncident(null)
        setEditIssues('')
        setEditComments('')
    }

    const handleSaveEdit = () => {
        if (!editIssues.trim()) {
            toast.error("Incident details cannot be empty")
            return
        }

        if (!window.confirm("Are you sure you want to update this incident?")) {
            return
        }

        const updateData = {
            issues: editIssues.trim(),
            comments: editComments.trim()
        }

        incidentResources.put(selectedIncident.id, updateData).then(res => {
            if (res.error && res.error.length > 0) {
                toast.error("Failed to update incident: " + res.error)
            } else {
                toast.success("Incident updated successfully")
                setEditingIncident(null)

                // Reload incidents
                loadIncidents().then(response => {
                    if (response['body']) {
                        setIncidents(response['body'])
                        // Update selected incident
                        const updated = response['body'].find(i => i.id === selectedIncident.id)
                        if (updated) {
                            setSelectedIncident(updated)
                        }
                    }
                })
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    const handleAddComment = () => {
        if (!newComment.trim()) {
            toast.error("Please enter a comment")
            return
        }

        if (!window.confirm("Are you sure you want to add this comment?")) {
            return
        }

        // Append new comment to existing comments
        const existingComments = (selectedIncident.comments && selectedIncident.comments !== 'None') ? selectedIncident.comments : ''
        const timestamp = moment().format('DD-MM-YYYY HH:mm')
        const userName = `${loggedInUser.firstname} ${loggedInUser.lastname}`
        const commentWithMeta = `[${timestamp} - ${userName}]: ${newComment.trim()}`
        const updatedComments = existingComments
            ? `${existingComments}\n\n${commentWithMeta}`
            : commentWithMeta

        const updateData = {
            comments: updatedComments
        }

        incidentResources.put(selectedIncident.id, updateData).then(res => {
            if (res.error && res.error.length > 0) {
                toast.error("Failed to add comment: " + res.error)
            } else {
                toast.success("Comment added successfully")
                setNewComment('')

                // Reload incidents
                loadIncidents().then(response => {
                    if (response['body']) {
                        setIncidents(response['body'])
                        const updated = response['body'].find(i => i.id === selectedIncident.id)
                        if (updated) {
                            setSelectedIncident(updated)
                        }
                    }
                })
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    const handleMarkAsReviewed = () => {
        if (!window.confirm("Are you sure you want to mark this incident as reviewed?")) {
            return
        }

        const updateData = {
            reviewed: true
        }

        incidentResources.put(selectedIncident.id, updateData).then(res => {
            if (res.error && res.error.length > 0) {
                toast.error("Failed to mark as reviewed: " + res.error)
            } else {
                toast.success("Incident marked as reviewed")

                // Reload incidents
                loadIncidents().then(response => {
                    if (response['body']) {
                        setIncidents(response['body'])
                        const updated = response['body'].find(i => i.id === selectedIncident.id)
                        if (updated) {
                            setSelectedIncident(updated)
                        } else {
                            // Incident removed from filtered list, close dialog
                            handleCloseDetails()
                        }
                    }
                })
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

      const handleUnmarkAsReviewed = () => {
        if (!window.confirm("Are you sure you want to mark this incident as unreviewed?")) {
            return
        }

        const updateData = {
            reviewed: false
        }

        incidentResources.put(selectedIncident.id, updateData).then(res => {
            if (res.error && res.error.length > 0) {
                toast.error("Failed to mark as unreviewed: " + res.error)
            } else {
                toast.success("Incident marked as unreviewed")

                // Reload incidents
                loadIncidents().then(response => {
                    if (response['body']) {
                        setIncidents(response['body'])
                        const updated = response['body'].find(i => i.id === selectedIncident.id)
                        if (updated) {
                            setSelectedIncident(updated)
                        }
                    }
                })
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    const handleDeleteIncident = () => {
        if (!window.confirm("Are you sure you want to delete this incident? This action cannot be undone.")) {
            return
        }

        incidentResources.delete(selectedIncident.id).then(res => {
            if (res.error && res.error.length > 0) {
                toast.error("Failed to delete incident: " + res.error)
            } else {
                toast.success("Incident deleted successfully")
                handleCloseDetails()

                // Reload incidents
                loadIncidents().then(response => {
                    if (response['body']) {
                        setIncidents(response['body'])
                    }
                })
            }
        }).catch((err) => {
            toast.error("Network error: " + err)
        })
    }

    return(
        <Fragment>
            <Topbar/>

            <div className="hero-area">
                <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">
                        <div className="page-banner-text">
                            {(isLoggedIn && loggedInUser['role.name'] === 'admin')?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Manage Incidents</strong></h1>
                            :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div id="main-container">
                {
                    (isLoggedIn && loggedInUser['role.name'] === 'admin') ?

                <div className="content">
                    <div className="container">
                        <div className="spacer-40"></div>

                        {/* Filters Section */}
                        <Paper style={{padding: '20px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px'}}>
                            <h3 style={{color: '#003165', marginBottom: '20px', fontSize: 'clamp(16px, 4vw, 18px)'}}>Filter Incidents</h3>

                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px'}}>
                                {/* Date Range */}
                                <div style={{flex: '1 1 200px', minWidth: '180px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)', marginBottom: '6px', display: 'block'}}>
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        max={moment().format('YYYY-MM-DD')}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{flex: '1 1 200px', minWidth: '180px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)', marginBottom: '6px', display: 'block'}}>
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        max={moment().format('YYYY-MM-DD')}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: 'clamp(13px, 3.5vw, 14px)',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                {/* Location Filter */}
                                <div style={{flex: '1 1 250px', minWidth: '220px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)', marginBottom: '6px', display: 'block'}}>
                                        Location (Optional)
                                    </label>
                                    <Select
                                        value={selectedLocation}
                                        onChange={handleLocationChange}
                                        options={locations}
                                        placeholder="All locations..."
                                        isClearable
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                fontSize: 'clamp(13px, 3.5vw, 14px)',
                                                minHeight: '38px'
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                fontSize: 'clamp(13px, 3.5vw, 14px)'
                                            })
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Unreviewed Only Checkbox */}
                            <div style={{marginBottom: '15px'}}>
                                <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>
                                    <input
                                        type="checkbox"
                                        checked={showUnreviewedOnly}
                                        onChange={(e) => setShowUnreviewedOnly(e.target.checked)}
                                        style={{marginRight: '8px', cursor: 'pointer', width: '18px', height: '18px'}}
                                    />
                                    <span style={{color: '#003165', fontWeight: 'bold'}}>Show unreviewed incidents only</span>
                                </label>
                            </div>

                            {/* Apply Filters Button */}
                            <button
                                onClick={handleFilterChange}
                                style={{
                                    backgroundColor: '#35ab7c',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '10px 25px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: 'clamp(13px, 3.5vw, 14px)'
                                }}
                            >
                                Apply Filters
                            </button>
                        </Paper>

                        {/* Incidents Table */}
                        <TableContainer component={Paper} style={{boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px'}}>
                            <Table>
                                <TableHead>
                                    <TableRow style={{backgroundColor: '#003165'}}>
                                        <TableCell style={{color: '#fff', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>Date</TableCell>
                                        <TableCell style={{color: '#fff', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>Location</TableCell>
                                        <TableCell style={{color: '#fff', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>Service</TableCell>
                                        <TableCell style={{color: '#fff', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>Issues (Preview)</TableCell>
                                        <TableCell style={{color: '#fff', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>Status</TableCell>
                                        <TableCell style={{color: '#fff', fontWeight: 'bold', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {incidents.length > 0 ? (
                                        incidents.map((incident, idx) => (
                                            <TableRow key={incident.id} style={{backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa'}}>
                                                <TableCell style={{fontSize: 'clamp(13px, 3.5vw, 14px)'}}>
                                                    {moment(incident.incident_date).format('DD-MM-YYYY HH:mm')}
                                                </TableCell>
                                                <TableCell style={{fontSize: 'clamp(13px, 3.5vw, 14px)'}}>
                                                    {incident['location.name'] || 'N/A'}
                                                </TableCell>
                                                <TableCell style={{fontSize: 'clamp(13px, 3.5vw, 14px)'}}>
                                                    {incident['service.name'] || 'N/A'}
                                                </TableCell>
                                                <TableCell style={{fontSize: 'clamp(13px, 3.5vw, 14px)', maxWidth: '300px'}}>
                                                    {incident.issues ?
                                                        (incident.issues.length > 80 ? incident.issues.substring(0, 80) + '...' : incident.issues)
                                                        : 'No details'}
                                                </TableCell>
                                                <TableCell>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 10px',
                                                        backgroundColor: incident.reviewed === 'True' ? '#d4edda' : '#fff3cd',
                                                        color: incident.reviewed === 'True' ? '#155724' : '#856404',
                                                        borderRadius: '12px',
                                                        fontSize: 'clamp(11px, 3vw, 12px)',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {incident.reviewed === 'True' ? 'Reviewed' : 'Pending'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <button
                                                        onClick={() => handleViewDetails(incident)}
                                                        style={{
                                                            backgroundColor: '#007bff',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '5px 12px',
                                                            cursor: 'pointer',
                                                            fontSize: 'clamp(12px, 3.2vw, 13px)'
                                                        }}
                                                    >
                                                        View Details
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} style={{textAlign: 'center', padding: '30px', fontSize: 'clamp(13px, 3.5vw, 14px)', color: '#666'}}>
                                                No incidents found matching the selected filters.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div className="spacer-50"></div>
                    </div>
                </div> : <></>
                }
            </div>

            {/* Incident Details Dialog */}
            {selectedIncident && (
                <Dialog
                    open={showDetailsDialog}
                    onClose={handleCloseDetails}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle style={{backgroundColor: '#003165', color: '#ffa800', fontWeight: 'bold'}}>
                        Incident Details
                    </DialogTitle>
                    <DialogContent style={{marginTop: '20px'}}>
                        <div style={{marginBottom: '15px'}}>
                            <strong style={{color: '#003165'}}>Date & Time:</strong>
                            <div style={{marginTop: '5px', color: '#495057'}}>
                                {moment(selectedIncident.incident_date).format('DD-MM-YYYY HH:mm')}
                            </div>
                        </div>

                        <div style={{marginBottom: '15px'}}>
                            <strong style={{color: '#003165'}}>Location:</strong>
                            <div style={{marginTop: '5px', color: '#495057'}}>
                                {selectedIncident['location.name'] || 'N/A'}
                            </div>
                        </div>

                        <div style={{marginBottom: '15px'}}>
                            <strong style={{color: '#003165'}}>Service:</strong>
                            <div style={{marginTop: '5px', color: '#495057'}}>
                                {selectedIncident['service.name'] || 'N/A'}
                            </div>
                        </div>

                        <div style={{marginBottom: '15px'}}>
                            <strong style={{color: '#003165'}}>Entered By:</strong>
                            <div style={{marginTop: '5px', color: '#495057'}}>
                                {selectedIncident['entered_by.firstname'] && selectedIncident['entered_by.lastname']
                                    ? `${selectedIncident['entered_by.firstname']} ${selectedIncident['entered_by.lastname']}`
                                    : 'N/A'}
                            </div>
                        </div>

                        <div style={{marginBottom: '15px'}}>
                            <strong style={{color: '#003165'}}>Status:</strong>
                            <div style={{marginTop: '5px'}}>
                                <span style={{
                                    display: 'inline-block',
                                    padding: '6px 14px',
                                    backgroundColor: selectedIncident.reviewed === 'True' ? '#d4edda' : '#fff3cd',
                                    color: selectedIncident.reviewed === 'True' ? '#155724' : '#856404',
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    {selectedIncident.reviewed === 'True' ? 'Reviewed' : 'Pending Review'}
                                </span>
                            </div>
                        </div>

                        <div style={{marginBottom: '20px'}}>
                            <strong style={{color: '#003165'}}>Incident Details:</strong>
                            {editingIncident === selectedIncident.id ? (
                                <textarea
                                    value={editIssues}
                                    onChange={(e) => setEditIssues(e.target.value)}
                                    rows={6}
                                    style={{
                                        width: '100%',
                                        marginTop: '8px',
                                        padding: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    color: '#495057',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '14px'
                                }}>
                                    {selectedIncident.issues || 'No details provided'}
                                </div>
                            )}
                        </div>

                        <div style={{marginBottom: '20px'}}>
                            <strong style={{color: '#003165'}}>Comments:</strong>
                            {editingIncident === selectedIncident.id ? (
                                <textarea
                                    value={editComments}
                                    onChange={(e) => setEditComments(e.target.value)}
                                    rows={6}
                                    placeholder="No comments yet..."
                                    style={{
                                        width: '100%',
                                        marginTop: '8px',
                                        padding: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    color: '#495057',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: '14px',
                                    minHeight: '60px'
                                }}>
                                    {(selectedIncident.comments && selectedIncident.comments !== 'None') ? selectedIncident.comments : 'No comments yet'}
                                </div>
                            )}
                        </div>

                        {/* Add Comment Section */}
                        {!editingIncident && (
                            <div style={{marginBottom: '20px', paddingTop: '15px', borderTop: '2px solid #e0e0e0'}}>
                                <strong style={{color: '#003165'}}>Add New Comment:</strong>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Type your comment here..."
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        marginTop: '8px',
                                        padding: '10px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit'
                                    }}
                                />
                                <button
                                    onClick={handleAddComment}
                                    style={{
                                        marginTop: '10px',
                                        backgroundColor: '#17a2b8',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 20px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                    }}
                                >
                                    Add Comment
                                </button>
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions style={{padding: '15px 24px', borderTop: '1px solid #e0e0e0'}}>
                        {editingIncident === selectedIncident.id ? (
                            <>
                                <button
                                    onClick={handleCancelEdit}
                                    style={{
                                        backgroundColor: '#6c757d',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 20px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    style={{
                                        backgroundColor: '#35ab7c',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 20px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '14px'
                                    }}
                                >
                                    Save Changes
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleCloseDetails}
                                    style={{
                                        backgroundColor: '#6c757d',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 20px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleStartEdit}
                                    style={{
                                        backgroundColor: '#ffa800',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 20px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Edit
                                </button>
                                {selectedIncident.reviewed === 'True' ? (
                                    <button
                                        onClick={handleUnmarkAsReviewed}
                                        style={{
                                            backgroundColor: '#ffc107',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 20px',
                                            cursor: 'pointer',
                                            fontSize: '14px'
                                        }}
                                    >
                                        Mark as Unreviewed
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleMarkAsReviewed}
                                        style={{
                                            backgroundColor: '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '8px 20px',
                                            cursor: 'pointer',
                                            fontSize: '14px'
                                        }}
                                    >
                                        Mark as Reviewed
                                    </button>
                                )}
                                <button
                                    onClick={handleDeleteIncident}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 20px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Delete
                                </button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            )}

        </Fragment>
    )
}


const mapStateToProps = (state) => {
    return{
       isLoggedIn: state.authReducer.isLoggedIn,
       loggedInUser: state.authReducer.user
    };
    
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewIncident);